/**
 * Subscription Page
 * 
 * Shows current tier, features, limits, and usage.
 * Provides upgrade CTA for lower tiers.
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { 
  Check, X, Lock, Sparkles, Crown, Shield, 
  Users, Plane, Battery, FileText, MapPin,
  Mail, ExternalLink, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const Subscription = () => {
  const { language, t } = useLanguage();
  const { 
    tier, 
    tierInfo, 
    features, 
    limits, 
    usage, 
    allFeatures,
    loading, 
    isPlatformAdmin,
    hasFeature,
    getUsage
  } = useFeatureFlag();

  const sub = t.subscription || {};

  const tierConfig = {
    free: {
      icon: Shield,
      color: 'slate',
      gradient: 'from-slate-500 to-slate-600',
      badge: 'bg-slate-100 text-slate-700 border-slate-300'
    },
    professional: {
      icon: Sparkles,
      color: 'blue',
      gradient: 'from-blue-500 to-indigo-600',
      badge: 'bg-blue-50 text-blue-700 border-blue-300'
    },
    enterprise: {
      icon: Crown,
      color: 'purple',
      gradient: 'from-purple-500 to-indigo-600',
      badge: 'bg-purple-50 text-purple-700 border-purple-300'
    }
  };

  const resourceIcons = {
    pilots: Users,
    aircraft: Plane,
    batteries: Battery,
    missions: MapPin,
    documents: FileText
  };

  const currentTierConfig = tierConfig[tier] || tierConfig.free;
  const TierIcon = currentTierConfig.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">
          {sub.title || 'Subscription'}
        </h1>
        <p className="text-slate-500 mt-1">
          {sub.yourPlan || 'Your Plan'}
        </p>
      </div>

      {/* Current Plan Card */}
      <Card className="overflow-hidden">
        <div className={`bg-gradient-to-r ${currentTierConfig.gradient} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <TierIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-white/80 text-sm">{sub.currentPlan || 'Current Plan'}</p>
                <h2 className="text-2xl font-bold">
                  {sub.tiers?.[tier] || tierInfo?.name || tier}
                </h2>
              </div>
            </div>
            {isPlatformAdmin && (
              <Badge className="bg-white/20 text-white border-white/30">
                Platform Admin
              </Badge>
            )}
          </div>
          <p className="mt-4 text-white/80">
            {sub.tierDescriptions?.[tier] || tierInfo?.description || ''}
          </p>
        </div>
      </Card>

      {/* Usage & Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Battery className="h-5 w-5 text-slate-500" />
            {sub.limits || 'Limits'} & {sub.usage || 'Usage'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.keys(limits).map(resource => {
              const Icon = resourceIcons[resource] || FileText;
              const usageInfo = getUsage(resource);
              const label = sub.resourceLabels?.[resource] || resource;
              const percentage = usageInfo.limit ? (usageInfo.current / usageInfo.limit) * 100 : 0;
              
              return (
                <div 
                  key={resource}
                  className="p-4 rounded-lg border border-slate-200 bg-slate-50/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <span className="font-medium text-slate-700">{label}</span>
                    </div>
                    <span className="text-sm text-slate-500">
                      {usageInfo.current} / {usageInfo.isUnlimited ? '∞' : usageInfo.limit}
                    </span>
                  </div>
                  {!usageInfo.isUnlimited && (
                    <Progress 
                      value={percentage} 
                      className={`h-2 ${usageInfo.isAtLimit ? 'bg-red-100' : 'bg-slate-200'}`}
                    />
                  )}
                  {usageInfo.isUnlimited && (
                    <div className="text-xs text-emerald-600 font-medium">
                      {t.common?.unlimited || 'Unlimited'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-slate-500" />
            {sub.features || 'Features'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {Object.entries(allFeatures).map(([featureKey, featureLabel]) => {
              const isEnabled = hasFeature(featureKey);
              const localLabel = sub.featureLabels?.[featureKey] || featureLabel;
              
              return (
                <div 
                  key={featureKey}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isEnabled 
                      ? 'border-emerald-200 bg-emerald-50/50' 
                      : 'border-slate-200 bg-slate-50/50'
                  }`}
                >
                  {isEnabled ? (
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-emerald-600" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                      <Lock className="h-3 w-3 text-slate-400" />
                    </div>
                  )}
                  <span className={isEnabled ? 'text-slate-700' : 'text-slate-400'}>
                    {localLabel}
                  </span>
                  {!isEnabled && (
                    <Badge variant="outline" className="ml-auto text-xs bg-amber-50 text-amber-700 border-amber-200">
                      Pro
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upgrade CTA (only for non-enterprise) */}
      {tier !== 'enterprise' && !isPlatformAdmin && (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    {sub.upgradeTitle || 'Upgrade Your Plan'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {sub.upgradeDescription || 'Unlock more features and remove limits'}
                  </p>
                </div>
              </div>
              <Button 
                className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md"
                onClick={() => window.location.href = `mailto:${sub.contactEmail || 'sales@fleetora.com'}?subject=Fleetora Upgrade Request`}
              >
                <Mail className="h-4 w-4" />
                {sub.contactUs || 'Contact Us to Upgrade'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Subscription;
